import type { StaffRole, UserDetail } from '@planuze/admin-contracts';
import {
  Button,
  EmptyState,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@planuze/ui';
import { Smartphone } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useUsersContent } from '../../../content/sections/users.js';
import { fmtDateTime } from '../../../lib/format.js';
import { SegmentedControl } from '../../filters/segmented-control.js';
import { CooldownDeviceModal } from '../modals/cooldown-device-modal.js';

type Device = UserDetail['devices'][number];
const MANAGE_ROLES: readonly StaffRole[] = ['super_admin', 'support'];

/* Status derivado do device — fonte única para badge e filtro: cooldown vence quando há um
   `blockedUntil` no futuro; senão `suspect`; senão ativo. Um `blockedUntil` já vencido volta
   a contar como ativo/suspeito (o cooldown expirou). */
type DeviceStatus = 'ativo' | 'suspeito' | 'cooldown';
function deviceStatus(device: Device, now: number): DeviceStatus {
  if (device.blockedUntil !== null && new Date(device.blockedUntil).getTime() > now) {
    return 'cooldown';
  }
  if (device.suspect) return 'suspeito';
  return 'ativo';
}

type DeviceFilter = 'todos' | DeviceStatus;

interface Props {
  readonly devices: readonly Device[];
  readonly userId: string;
  readonly staffRole: StaffRole;
}

export function DevicesTab({ devices, userId, staffRole }: Props) {
  const copy = useUsersContent();
  const [cooldownDeviceId, setCooldownDeviceId] = useState<string | null>(null);
  const [filter, setFilter] = useState<DeviceFilter>('todos');
  const canManage = MANAGE_ROLES.includes(staffRole);

  const { rows, counts } = useMemo(() => {
    const now = Date.now();
    const withStatus = devices.map((device) => ({ device, status: deviceStatus(device, now) }));
    return {
      counts: {
        todos: withStatus.length,
        ativo: withStatus.filter((r) => r.status === 'ativo').length,
        suspeito: withStatus.filter((r) => r.status === 'suspeito').length,
        cooldown: withStatus.filter((r) => r.status === 'cooldown').length,
      },
      rows: filter === 'todos' ? withStatus : withStatus.filter((r) => r.status === filter),
    };
  }, [devices, filter]);

  if (devices.length === 0) {
    return <EmptyState icon={<Smartphone className="size-6" />} title={copy.devicesTab.empty} />;
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <SegmentedControl<DeviceFilter>
          ariaLabel={copy.devicesTab.filterAriaLabel}
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'todos', label: copy.devicesTab.optionAll, count: counts.todos },
            { value: 'ativo', label: copy.devicesTab.optionActive, count: counts.ativo },
            { value: 'suspeito', label: copy.devicesTab.optionSuspect, count: counts.suspeito },
            { value: 'cooldown', label: copy.devicesTab.optionCooldown, count: counts.cooldown },
          ]}
        />
      </div>
      {rows.length === 0 ? (
        <EmptyState
          icon={<Smartphone className="size-6" />}
          title={copy.common.noResultsTitle}
          description={copy.devicesTab.emptyFilteredDescription}
          primaryAction={
            <Button variant="outline" onClick={() => setFilter('todos')}>
              {copy.common.clearFilters}
            </Button>
          }
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{copy.devicesTab.columnDevice}</TableHead>
              <TableHead>{copy.devicesTab.columnVersion}</TableHead>
              <TableHead>{copy.devicesTab.columnLastSeen}</TableHead>
              <TableHead>{copy.devicesTab.columnStatus}</TableHead>
              {canManage && <TableHead />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(({ device, status }) => (
              <TableRow key={device.deviceId}>
                <TableCell>
                  <code className="text-xs text-muted-foreground">
                    {device.deviceId.slice(0, 12)}…
                  </code>
                </TableCell>
                <TableCell className="text-muted-foreground">{device.appVersion}</TableCell>
                <TableCell className="text-muted-foreground">
                  {fmtDateTime(device.lastSeenAt)}
                </TableCell>
                <TableCell>
                  {status === 'cooldown' ? (
                    <StatusBadge variant="destructive">
                      {copy.devicesTab.statusCooldown}
                    </StatusBadge>
                  ) : status === 'suspeito' ? (
                    <StatusBadge variant="warning">{copy.devicesTab.statusSuspect}</StatusBadge>
                  ) : (
                    <StatusBadge variant="success">{copy.devicesTab.statusActive}</StatusBadge>
                  )}
                </TableCell>
                {canManage && (
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setCooldownDeviceId(device.deviceId)}
                    >
                      {copy.devicesTab.cooldownAction}
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {cooldownDeviceId !== null && (
        <CooldownDeviceModal
          open
          onClose={() => setCooldownDeviceId(null)}
          userId={userId}
          deviceId={cooldownDeviceId}
        />
      )}
    </div>
  );
}
